import React from 'react';
import { Navigate } from 'react-router-dom';
import { hasRole } from '../config/roles';
import { getCurrentUser } from '../utils/api';
import useRolePermissions, { canAccessRoute } from '../hooks/useRolePermissions';

/**
 * `dynamicPathKey` is optional and only passed on routes an admin can
 * reconfigure from Role & Path Management (currently just /installation and
 * /installation/scan). Without it, this component is byte-for-byte the same
 * synchronous check every other route already used -- no new network call,
 * no new render state. With it, `allowedRoles` becomes the fallback used
 * only until an admin has explicitly saved route access for the current
 * role on that page (see useRolePermissions.js's docblock for why this is
 * partitioned separately from any other permission the role has saved).
 */
export default function ProtectedRoute({ allowedRoles, children, dynamicPathKey }) {
  const user = getCurrentUser();
  const perm = useRolePermissions(user?.role, { enabled: !!dynamicPathKey });

  if (!user) return <Navigate to="/login" />;

  if (!dynamicPathKey) {
    if (!hasRole(user.role, allowedRoles)) {
      return <Navigate to="/unauthorized" />;
    }
    return children;
  }

  // Avoid a flash of protected content while the role-path check is
  // in flight -- render nothing for the brief moment it takes to resolve.
  if (perm.loading) return null;

  if (!canAccessRoute(dynamicPathKey, perm, user.role, allowedRoles)) {
    return <Navigate to="/unauthorized" />;
  }
  return children;
}
